import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock, Gauge, AlertTriangle, Moon } from "lucide-react";

// Post-calculation route insights: geocoding warnings, better-order suggestion,
// miles/drive-time summary, overnight suggestion and the per-leg breakdown.
export const RouteInsights = ({
  routeData,
  orderSuggestion,
  onApplyOrder,
  loading,
  totalRouteHours,
  displayMiles,
  driveTime,
}) => {
  const fuzzyPoints = (routeData?.points || []).filter((p) => p.display?.includes("nearest match"));
  const suggestedNights = routeData ? Math.floor(totalRouteHours / 10) : 0;

  return (
    <>
      {fuzzyPoints.length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-300 p-2.5 space-y-1.5" data-testid="nearest-match-warning">
          {fuzzyPoints.map((p) => (
            <p key={p.address} className="flex items-start gap-1.5 text-xs text-amber-800 leading-snug">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Exact address “{p.address}” wasn't found — using nearest match:{" "}
                <span className="font-semibold">{p.display.replace(/ \(nearest match for .*\)$/, "")}</span>.
                Mileage may differ slightly.
              </span>
            </p>
          ))}
        </div>
      )}

      {orderSuggestion && (
        <div className="rounded-md bg-[#FFF7ED] border border-[#F97316]/40 p-3" data-testid="route-order-suggestion">
          <p className="text-xs font-bold uppercase tracking-wide text-[#EA580C]">Better route order suggested</p>
          <p className="text-xs text-[#1C1917] mt-1">
            Going to the closest drop first:{" "}
            {orderSuggestion
              .map((origIdx, i) => `${i + 1}. ${routeData.points[origIdx + 1]?.address || `Stop ${origIdx + 1}`}`)
              .join(" → ")}
          </p>
          <Button
            size="sm"
            onClick={onApplyOrder}
            disabled={loading}
            className="mt-2 h-7 text-xs bg-[#F97316] hover:bg-[#EA580C] text-white font-bold"
            data-testid="apply-route-order"
          >
            Apply order &amp; recalculate
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 pt-1">
        <div className="rounded-lg bg-[#FFF7ED] border border-[#F97316]/30 p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-[#EA580C]">
            <Gauge className="h-3.5 w-3.5" /> Total Miles
          </div>
          <div className="text-2xl font-black text-[#1C1917]" data-testid="total-miles">
            {displayMiles != null ? displayMiles : "—"}
          </div>
        </div>
        <div className="rounded-lg bg-stone-100 border border-border p-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-[#57534E]">
            <Clock className="h-3.5 w-3.5" /> Drive Time
          </div>
          <div className="text-2xl font-black text-[#1C1917]" data-testid="drive-time">
            {driveTime}
          </div>
        </div>
      </div>

      {suggestedNights > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-300 p-2.5 flex items-start gap-1.5" data-testid="overnight-suggestion">
          <Moon className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-700" />
          <p className="text-xs text-amber-800 leading-snug">
            ~{totalRouteHours.toFixed(1)}h total route time — suggest{" "}
            <b>{suggestedNights} overnight stay{suggestedNights > 1 ? "s" : ""}</b> (1 per 10 driving hours).
            Enable Overnight in the In-House options to include hotel &amp; per diem.
          </p>
        </div>
      )}
    </>
  );
};

export const LegBreakdown = ({ legs }) =>
  legs?.length > 0 ? (
    <div className="pt-1">
      <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
        Per-Leg Breakdown
      </Label>
      <div className="mt-2 space-y-1" data-testid="leg-breakdown">
        {legs.map((leg) => (
          <div key={`${leg.from}-${leg.to}`} className="flex justify-between text-sm py-1 border-b border-border/60">
            <span className="text-[#1C1917] truncate mr-2">
              {leg.from} → {leg.to}
            </span>
            <span className="text-[#57534E] whitespace-nowrap font-medium">
              {leg.miles} mi <span className="text-stone-400">({leg.running_miles} total)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  ) : null;
